import type { HandoffRedemption } from "./impersonation-handoff";

// sessionStorage is isolated per tab: reloading one user must never replace
// another tab's identity. Keep an ended marker so expiry fails closed on reload.
const KEY = "authe:dashboard:impersonation:v1";
export type StoredImpersonation = HandoffRedemption | { ended: true };

export function readImpersonation(storage: Storage = sessionStorage): StoredImpersonation | null {
  const raw = storage.getItem(KEY);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as HandoffRedemption;
    const expires = Date.parse(value.expiresAt ?? "");
    if (!value.tokens?.access_token || !value.tokens?.id_token || !Number.isFinite(expires) || expires <= Date.now()) {
      endImpersonation(storage);
      return { ended: true };
    }
    return value;
  } catch {
    endImpersonation(storage);
    return { ended: true };
  }
}

export function saveImpersonation(session: HandoffRedemption, storage: Storage = sessionStorage): void {
  storage.setItem(KEY, JSON.stringify(session));
}

export function endImpersonation(storage: Storage = sessionStorage): void {
  storage.setItem(KEY, JSON.stringify({ ended: true }));
}

/**
 * Forget the session entirely, so this tab is an ordinary dashboard again.
 *
 * Distinct from {@link endImpersonation}, which leaves the `{ended: true}`
 * marker behind ON PURPOSE: a reload after an impersonation ends must fail
 * closed and say so, never fall silently through to the operator's own account.
 * But the marker is durable, so every later reload hit the same wall — the
 * bootstrap screen's "Reload dashboard" could not recover the tab, only repeat
 * the failure. Removing it is therefore an EXPLICIT act, taken when the person
 * has read the notice and asked to go back to being themselves.
 */
export function clearImpersonation(storage: Storage = sessionStorage): void {
  storage.removeItem(KEY);
}
